<article class="doc">
  <header class="doc__header">
    <p class="doc__eyebrow">Atom</p>
    <h1 class="doc__title">Checkbox</h1>
    <p class="doc__lead">
      Casilla de verificación custom con checkmark SVG, estado indeterminate,
      descripción secundaria y mensaje de error controlable.
      Sin apariencia nativa — 100% CSS. Borde normal <code>#dce0e8</code>,
      activo <code>#4169e2</code>.
    </p>
  </header>

  <!-- ── Estados ─────────────────────────────── -->
  <h2 class="doc-section-title">Estados</h2>

  <div class="doc-grid doc-grid--sm">
    <app-example-section title="Sin marcar" description="Estado inicial por defecto.">
      <ui-checkbox label="Aceptar términos y condiciones" />
    </app-example-section>

    <app-example-section title="Marcado" description="checked=true.">
      <ui-checkbox label="Recibir newsletter" [checked]="true" />
    </app-example-section>

    <app-example-section
      title="Indeterminate"
      description="Útil para 'Seleccionar todo' cuando el grupo está parcialmente seleccionado."
    >
      <ui-checkbox label="Seleccionar todo" [indeterminate]="true" />
    </app-example-section>

    <app-example-section title="Deshabilitado sin marcar" description="Sin interacción.">
      <ui-checkbox label="Opción no disponible" [disabled]="true" />
    </app-example-section>

    <app-example-section title="Deshabilitado marcado" description="Valor fijo, sin interacción. Azul tenue.">
      <ui-checkbox label="Configuración de sistema" [checked]="true" [disabled]="true" />
    </app-example-section>

    <app-example-section
      title="Error / inválido"
      description="Borde rojo. showMessage controla el texto de error."
    >
      <div class="doc-example-stack">
        <ui-checkbox
          label="Acepto los términos"
          [required]="true"
          [invalid]="isInvalid()"
          message="Debes aceptar los términos para continuar."
          [showMessage]="showErrorMsg()"
          [(checked)]="acceptTerms"
        />
        <div class="doc-toggle-row">
          <button type="button" class="doc-native-toggle" (click)="toggleInvalid()">
            {{ isInvalid() ? 'Quitar error' : 'Activar error' }}
          </button>
          <button type="button" class="doc-native-toggle" (click)="toggleErrorMsg()">
            {{ showErrorMsg() ? 'Ocultar mensaje' : 'Mostrar mensaje' }}
          </button>
        </div>
      </div>
    </app-example-section>
  </div>

  <!-- ── Variantes ───────────────────────────── -->
  <h2 class="doc-section-title">Variantes</h2>

  <div class="doc-grid doc-grid--sm">
    <app-example-section
      title="Deshabilitado marcado (locked)"
      description="Sin interacción, con el mismo azul que Marcado."
    >
      <ui-checkbox
        label="Punto de entrega verificado"
        [checked]="true"
        appearance="locked"
      />
    </app-example-section>

    <app-example-section
      title="Con descripción"
      description="Texto secundario bajo el label para contexto adicional."
    >
      <ui-checkbox
        label="Activar notificaciones"
        description="Recibirás alertas sobre actividad relevante en tu cuenta."
      />
    </app-example-section>

    <app-example-section
      title="Con badge Obligatorio"
      description="Indica que la selección es requerida."
    >
      <ui-checkbox
        label="Acepto la política de privacidad"
        [required]="true"
      />
    </app-example-section>

    <app-example-section
      title="Con badge Opcional"
      description="showOptionalHint muestra el badge."
    >
      <ui-checkbox
        label="Suscribirme al boletín"
        [showOptionalHint]="true"
      />
    </app-example-section>

    <app-example-section
      title="Binding bidireccional"
      description="Estado reactivo con signal."
    >
      <div class="doc-example-stack">
        <ui-checkbox
          label="Recibir newsletter"
          [(checked)]="newsletter"
        />
        <p class="doc-value-preview">
          Estado: <strong>{{ newsletter() ? 'marcado' : 'sin marcar' }}</strong>
        </p>
      </div>
    </app-example-section>
  </div>

  <!-- ── Ejemplos de uso ─────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Ejemplos de uso</h2>

    <p class="doc-block__p">Binding bidireccional con signal:</p>
    <pre class="doc-block__pre" role="code"><code>acceptTerms = signal(false);

&lt;ui-checkbox
  label="Acepto términos y condiciones"
  [required]="true"
  [(checked)]="acceptTerms"
/&gt;</code></pre>

    <p class="doc-block__p">Validación con Reactive Forms:</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-checkbox
  label="Acepto los términos"
  [required]="true"
  [checked]="form.controls.terms.value ?? false"
  (checkedChange)="form.controls.terms.setValue($event)"
  [invalid]="form.controls.terms.invalid &amp;&amp; form.controls.terms.touched"
  message="Debes aceptar para continuar."
/&gt;</code></pre>

    <p class="doc-block__p">Estado indeterminate para "Seleccionar todo":</p>
    <pre class="doc-block__pre" role="code"><code>allChecked = computed(() => items().every(i => i.checked));
someChecked = computed(() => items().some(i => i.checked));
isIndeterminate = computed(() => someChecked() &amp;&amp; !allChecked());

&lt;ui-checkbox
  label="Seleccionar todo"
  [checked]="allChecked()"
  [indeterminate]="isIndeterminate()"
  (checkedChange)="toggleAll($event)"
/&gt;</code></pre>
  </section>

  <!-- ── Inputs ──────────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Inputs</h2>
    <table class="doc-table">
      <thead>
        <tr><th>Nombre</th><th>Tipo</th><th>Default</th><th>Descripción</th></tr>
      </thead>
      <tbody>
        <tr><td><code>checked</code></td><td><code>boolean</code> (model)</td><td><code>false</code></td><td>Estado de la casilla. Soporta <code>[(checked)]</code>.</td></tr>
        <tr><td><code>indeterminate</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Estado parcial. Muestra guión en lugar de checkmark.</td></tr>
        <tr><td><code>label</code></td><td><code>string</code></td><td><code>''</code></td><td>Texto principal junto a la casilla.</td></tr>
        <tr><td><code>description</code></td><td><code>string | undefined</code></td><td><code>undefined</code></td><td>Texto secundario bajo el label.</td></tr>
        <tr><td><code>required</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Badge "Obligatorio" y <code>aria-required</code>.</td></tr>
        <tr><td><code>showOptionalHint</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Badge "Opcional".</td></tr>
        <tr><td><code>disabled</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Deshabilita la casilla.</td></tr>
        <tr><td><code>appearance</code></td><td><code>'default' | 'locked'</code></td><td><code>'default'</code></td><td><code>locked</code>: sin interacción y, si está marcado, el mismo azul que Marcado.</td></tr>
        <tr><td><code>invalid</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Borde rojo y <code>aria-invalid</code>.</td></tr>
        <tr><td><code>message</code></td><td><code>string | undefined</code></td><td><code>undefined</code></td><td>Texto del mensaje de error.</td></tr>
        <tr><td><code>showMessage</code></td><td><code>boolean</code></td><td><code>true</code></td><td>Controla si el mensaje se renderiza.</td></tr>
        <tr><td><code>name</code></td><td><code>string | undefined</code></td><td><code>undefined</code></td><td>Atributo <code>name</code> del input.</td></tr>
        <tr><td><code>value</code></td><td><code>string | undefined</code></td><td><code>undefined</code></td><td>Atributo <code>value</code> del input (útil en grupos).</td></tr>
        <tr><td><code>fieldId</code></td><td><code>string | undefined</code></td><td>Auto-generado</td><td>ID del input para la asociación con el label.</td></tr>
      </tbody>
    </table>
  </section>

  <!-- ── Outputs ─────────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Outputs</h2>
    <table class="doc-table">
      <thead>
        <tr><th>Nombre</th><th>Tipo</th><th>Descripción</th></tr>
      </thead>
      <tbody>
        <tr><td><code>checkedChange</code></td><td><code>boolean</code></td><td>Emite el nuevo estado al cambiar.</td></tr>
        <tr><td><code>blurred</code></td><td><code>FocusEvent</code></td><td>Emite cuando el input pierde el foco.</td></tr>
        <tr><td><code>focused</code></td><td><code>FocusEvent</code></td><td>Emite cuando el input recibe el foco.</td></tr>
      </tbody>
    </table>
  </section>

  <!-- ── Buenas prácticas ───────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Buenas prácticas</h2>
    <ul class="doc-list">
      <li>Siempre incluye un <code>label</code> descriptivo — es el texto accesible de la casilla.</li>
      <li>Usa <code>description</code> para contexto adicional sin sobrecargar el label.</li>
      <li>Activa <code>[invalid]</code> solo tras interacción del usuario (blur o submit).</li>
      <li>Para grupos de checkboxes (ej. filtros), usa el patrón "Seleccionar todo" con <code>[indeterminate]</code>.</li>
      <li>Para selección exclusiva (una sola opción), usa <code>ui-radio</code> en lugar de checkbox.</li>
    </ul>
  </section>
</article>
